import { DeleteTaskDialog } from "@/components/Dialogs/deleteTaskDialog.component";
import { DuplicateTaskDialog } from "@/components/Dialogs/duplicateTaskDialog.component";
import TaskCellAssignee from "@/components/SingleLineTask/SingleLineTaskCells/taskCellAssignee.component";
import TaskCellCollaborators from "@/components/SingleLineTask/SingleLineTaskCells/taskCellCollaborators.component";
import TaskCellDueDate from "@/components/SingleLineTask/SingleLineTaskCells/taskCellDueDate.component";
import TaskCellStatus from "@/components/SingleLineTask/SingleLineTaskCells/taskCellStatus.component";
import TaskCellTags from "@/components/SingleLineTask/SingleLineTaskCells/taskCellTags.component";
import TaskCellTitle from "@/components/SingleLineTask/SingleLineTaskCells/taskCellTitle.component";
import { ROUTE_SEARCH } from "@/constants/routes.constants";
import useChangeStatus from "@/hooks/SingleLineTask/useChangeStatus.hook";
import useDeleteTask from "@/hooks/SingleLineTask/useDeleteTask.hook";
import useDoubleClick from "@/hooks/SingleLineTask/useDoubleClick.hook";
import useDuplicateTask from "@/hooks/SingleLineTask/useDuplicateTask.hook";
import useFieldHandlers from "@/hooks/SingleLineTask/useFieldHandlers.hook";
import useHandleSave from "@/hooks/SingleLineTask/useHandleSave.hook";
import useOutsideClick from "@/hooks/SingleLineTask/useOutsideClick.hook";
import useSaveAllowed from "@/hooks/SingleLineTask/useSaveAllowed.hook";
import useSingleClick from "@/hooks/SingleLineTask/useSingleClick.hook";
import { AppContext } from "@/services/AppContext/appContext";
import { Button } from "@/shadcn/components/ui/button";
import { Checkbox } from "@/shadcn/components/ui/checkbox";
import {
  ContextMenu,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuTrigger,
} from "@/shadcn/components/ui/context-menu";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/shadcn/components/ui/dropdown-menu";
import { TableCell, TableRow } from "@/shadcn/components/ui/table";
import { EditedTask, SearchTaskAdvanced, SearchTaskBasic } from "@/types";
import { DotsHorizontalIcon } from "@radix-ui/react-icons";
import { Row } from "@tanstack/react-table";
import React, { useContext, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router";
type SearchTaskUnion = SearchTaskBasic | SearchTaskAdvanced;

type ViewSearchResultsRowProps = {
  row: Row<SearchTaskUnion>;
  visibleColumns: string[];
  inlineEditingDisabled?: boolean;
  bulkEditVersion?: number;
};

const ViewSearchResultsRow: React.FC<ViewSearchResultsRowProps> = ({
  row,
  visibleColumns,
  inlineEditingDisabled = false,
  bulkEditVersion = 0,
}) => {
  const nav = useNavigate();
  const location = useLocation();
  const { forceFetchAll, removeTaskFromUserTasks } = useContext(AppContext);
  const [isEditing, setIsEditing] = useState<boolean>(false);
  const [editedTask, setEditedTask] = useState<EditedTask | null>(null);
  const rowRef = useRef<HTMLTableRowElement>(null);
  const [clickTimeout, setClickTimeout] = useState<NodeJS.Timeout | null>(null);
  const saveAllowed = useRef<boolean>(false);
  const createTagOpen = useRef<boolean>(false);
  const createDueDateOpen = useRef<boolean>(false);
  const createCollaboratorRef = useRef<HTMLDivElement | null>(null);
  const [selectedEditableTask, setSelectedEditableTask] =
    useState<EditedTask | null>(null);
  const createAssigneeOpen = useRef<boolean>(false);
  // Dialogs
  const [isCopyDialogOpen, setIsCopyDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);

  const editedTaskRef = useRef(editedTask);
  // Update edited task across renders
  useEffect(() => {
    editedTaskRef.current = editedTask;
  }, [editedTask]);

  useEffect(() => {
    if (!inlineEditingDisabled) {
      return;
    }

    const hadUnsavedChanges = saveAllowed.current;

    if (clickTimeout) {
      clearTimeout(clickTimeout);
      setClickTimeout(null);
    }

    setIsEditing(false);
    if (rowRef.current) {
      rowRef.current.style.outline = "";
    }

    if (hadUnsavedChanges) {
      setEditedTask(selectedEditableTask ?? null);
    }

    saveAllowed.current = false;
  }, [
    clickTimeout,
    inlineEditingDisabled,
    selectedEditableTask,
    setClickTimeout,
    saveAllowed,
  ]);

  useEffect(() => {
    if (bulkEditVersion === 0) {
      return;
    }

    setClickTimeout((prevTimeout) => {
      if (prevTimeout) {
        clearTimeout(prevTimeout);
      }
      return null;
    });

    setIsEditing(false);
    setEditedTask(null);
    setSelectedEditableTask(null);
    saveAllowed.current = false;

    if (rowRef.current) {
      rowRef.current.style.outline = "";
    }
  }, [bulkEditVersion, setClickTimeout]);

  function openTaskDetails(taskID: number) {
    const searchParams = new URLSearchParams(location.search);
    const newPath = `${ROUTE_SEARCH}/${taskID}?${searchParams.toString()}`;
    nav(newPath);
  }

  function closeTaskDetails() {
    const searchParams = new URLSearchParams(location.search);
    const newPath = `${ROUTE_SEARCH}?${searchParams.toString()}`;
    nav(newPath);
  }

  const { completeTask, unCompleteTask } = useChangeStatus("PrecacheTasks");

  // #region Edit Task Fields

  const {
    handleFieldAssignedTo,
    handleEditField,
    handleFieldDueDate,
    handleFieldTags,
    handleFieldCollaborators,
  } = useFieldHandlers(editedTask, setEditedTask);

  // Sends updated task to the server
  const { handleSave } = useHandleSave(
    editedTask,
    "PrecacheTasks",
    saveAllowed,
    editedTaskRef,
    setIsEditing,
    setSelectedEditableTask
  );

  // #endregion Edit Task

  // #region Lifecycle
  // Checks if save is allowed: Something has changed and the title is not empty
  useSaveAllowed(editedTask, selectedEditableTask, saveAllowed);

  // #endregion Lifecycle

  // #region Click Handling Edit/View
  // Single click action (Editing)
  const { handleSingleClick } = useSingleClick(
    row.original,
    isEditing,
    selectedEditableTask,
    editedTask,
    setIsEditing,
    setSelectedEditableTask,
    setEditedTask,
    rowRef,
    clickTimeout,
    setClickTimeout
  );

  // Double click action (Open Task Details)
  const { handleDoubleClick } = useDoubleClick(
    row.original.id,
    clickTimeout,
    isEditing,
    editedTaskRef,
    saveAllowed,
    setIsEditing,
    setSelectedEditableTask,
    openTaskDetails,
    handleSave
  );

  // Handle clicks outside the row
  useOutsideClick(
    rowRef,
    createTagOpen,
    createDueDateOpen,
    createAssigneeOpen,
    saveAllowed,
    editedTaskRef,
    handleSave,
    setIsEditing
  );

  // #endregion Click Handling Edit/View

  // #region Task Handling
  const { duplicateTask } = useDuplicateTask({
    onTaskDuplicated: async (newTaskID) => {
      await forceFetchAll();
      openTaskDetails(newTaskID);
    },
  });

  const { deleteTask } = useDeleteTask({
    onTaskDeleted: async () => {
      removeTaskFromUserTasks(row.original.id);
      await forceFetchAll();
      if (window.location.pathname === `${ROUTE_SEARCH}/${row.original.id}`) {
        closeTaskDetails();
      }
    },
  });

  // #endregion Task Handling

  return (
    <ContextMenu>
      <ContextMenuTrigger asChild>
        <TableRow
          ref={rowRef}
          data-state={row.getIsSelected() && "selected"}
          onClick={() => {
            if (inlineEditingDisabled) {
              return;
            }
            handleSingleClick();
          }}
          onDoubleClick={async () => await handleDoubleClick()}
        >
          {visibleColumns.includes("select") && (
            <TableCell>
              <Checkbox
                checked={row.getIsSelected()}
                onCheckedChange={(value) => row.toggleSelected(!!value)}
                onMouseDown={(e) => {
                  e.stopPropagation();
                }}
                onClick={(e) => {
                  e.stopPropagation();
                }}
                aria-label="Select row"
              />
            </TableCell>
          )}
          {visibleColumns.includes("status") && (
            <TableCell>
              <TaskCellStatus
                className="capitalize select-none"
                completed={row.getValue("status") === "complete"}
                taskID={row.original.id}
                completeTask={completeTask}
                unCompleteTask={unCompleteTask}
              />
            </TableCell>
          )}
          {visibleColumns.includes("title") && (
            <TableCell>
              <TaskCellTitle
                className={
                  isEditing && editedTask ? "max-w-72" : "max-w-72 select-none"
                }
                editing={isEditing}
                editedTitle={editedTask ? editedTask.title : undefined}
                title={row.original.title}
                inputID="edit-title"
                handleEditField={handleEditField}
              />
            </TableCell>
          )}
          {visibleColumns.includes("due_date") && (
            <TableCell>
              <TaskCellDueDate
                className={isEditing && editedTask ? "" : " select-none"}
                dueDate={row.getValue("due_date")}
                editedDueDate={editedTask?.due_date || undefined}
                editing={isEditing}
                handleFieldDueDate={handleFieldDueDate}
                createDueDateOpen={createDueDateOpen}
              />
            </TableCell>
          )}
          {visibleColumns.includes("assignee_name") && (
            <TableCell>
              <TaskCellAssignee
                className={
                  isEditing && editedTask ? " " : "truncate select-none"
                }
                assignedTo={row.original.assignee_id}
                editedAssignedTo={editedTask?.assigned_to}
                editing={isEditing}
                handleFieldAssignedTo={handleFieldAssignedTo}
                isOpen={createAssigneeOpen}
              />
            </TableCell>
          )}
          {visibleColumns.includes("collaborators") && (
            <TableCell>
              <TaskCellCollaborators
                className={isEditing && editedTask ? "" : "select-none"}
                editing={isEditing}
                collaboratorIDs={row.original.collaborators}
                editedCollaboratorsIDs={editedTask?.collaborators || undefined}
                handleFieldCollaborators={handleFieldCollaborators}
                collaboratorRef={createCollaboratorRef}
              />
            </TableCell>
          )}
          {visibleColumns.includes("tags") && (
            <TableCell>
              <TaskCellTags
                className={
                  isEditing && editedTask ? "" : "w-full truncate select-none"
                }
                editing={isEditing}
                taskTags={row.getValue("tags")}
                editedTags={editedTask?.tags || undefined}
                selectedEditableTaskTags={
                  selectedEditableTask?.tags || undefined
                }
                handleFieldTags={handleFieldTags}
                createTagOpen={createTagOpen}
              />
            </TableCell>
          )}
          {visibleColumns.includes("actions") && (
            <TableCell>
              <div className="text-right">
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" className="h-8 w-8 p-0">
                      <span className="sr-only">Open menu</span>
                      <DotsHorizontalIcon className="h-4 w-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuLabel>Actions</DropdownMenuLabel>
                    <DropdownMenuItem
                      onClick={() =>
                        navigator.clipboard.writeText(
                          row.original.id.toString()
                        )
                      }
                    >
                      Copy task ID
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem
                      onClick={() => openTaskDetails(row.original.id)}
                    >
                      View task details
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            </TableCell>
          )}
        </TableRow>
      </ContextMenuTrigger>
      {/* Right Click Menu */}
      <ContextMenuContent className="outline outline-1 outline-gray-300 dark:outline-gray-700">
        <ContextMenuItem
          className="cursor-pointer"
          onClick={() => {
            openTaskDetails(row.original.id);
          }}
        >
          Open
        </ContextMenuItem>
        <ContextMenuItem
          className="cursor-pointer"
          onClick={() => {
            setIsCopyDialogOpen(true);
          }}
        >
          Duplicate Task
        </ContextMenuItem>
        <ContextMenuItem
          className="text-red-500 cursor-pointer"
          onClick={() => {
            setIsDeleteDialogOpen(true);
          }}
        >
          Delete
        </ContextMenuItem>
      </ContextMenuContent>

      {/* Copy Alert Dialog */}
      <DuplicateTaskDialog
        isOpen={isCopyDialogOpen}
        onClose={() => setIsCopyDialogOpen(false)}
        onDuplicate={async () => {
          await duplicateTask(row.original.id);
          setIsCopyDialogOpen(false);
        }}
      />

      {/* Delete Task Alert Dialog */}
      <DeleteTaskDialog
        isOpen={isDeleteDialogOpen}
        onClose={() => setIsDeleteDialogOpen(false)}
        onDelete={async () => {
          await deleteTask(row.original.id);
          setIsDeleteDialogOpen(false);
        }}
        type="single"
      />
    </ContextMenu>
  );
};

export default ViewSearchResultsRow;
